import * as React from "react";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import { getBaseUrl } from "../core/config";
import { useIntl } from "react-intl";
import { axiosInstance } from "../core/axios-interceptors";

interface DropdownOdataProps {
  url: string;
  fieldNameId: string;
  fieldNameDescription: string;
  value?: string | null;
  onChange: (newValue: string | null) => void;
  error: string | null;
  label?: string;
  isRequired?: boolean;
  initSelected?: any;
  top?: number; // Number of items to fetch (default: 100)
  searchField?: string; // Field to search on with contains filter
  debounceTime?: number; // Debounce time in milliseconds (default: 0, no debounce)
  preFilter?: string; // OData filter string to be applied before search filter
}

interface ODataItem {
  [key: string]: any;
}

export function DropdownOdata({
  url,
  fieldNameId,
  fieldNameDescription,
  value,
  onChange,
  error,
  label = "Select option",
  isRequired = false,
  initSelected,
  top = 100,
  searchField,
  debounceTime = 0,
  preFilter,
}: DropdownOdataProps) {
  const intl = useIntl();
  const [open, setOpen] = React.useState(false);
  const [options, setOptions] = React.useState<ODataItem[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [inputValue, setInputValue] = React.useState("");
  const [searchTerm, setSearchTerm] = React.useState("");
  const inputValueRef = React.useRef(inputValue);

  const [selectedOption, setSelectedOption] = React.useState<any>(null);
  const isSelectingRef = React.useRef(false);

  // Keep ref in sync with inputValue
  React.useEffect(() => {
    inputValueRef.current = inputValue;
  }, [inputValue]);

  React.useEffect(() => {
    setSelectedOption(
      options.length
        ? (options.find((opt) => opt[fieldNameId] === value) ?? null)
        : initSelected
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldNameId, options, value]);

  function oDataStandard(array: any[]): any[] {
    return array.map((d) =>
      Object.entries(d).reduce(
        (acc, [key, value]) => ({
          ...acc,
          [key.charAt(0).toLowerCase() + key.slice(1)]: value,
        }),
        {} as any
      )
    );
  }

  // Debounce effect for search
  React.useEffect(() => {
    if (debounceTime > 0 && searchField) {
      const timer = setTimeout(() => {
        setSearchTerm(inputValue);
      }, debounceTime);
      return () => clearTimeout(timer);
    } else {
      setSearchTerm(inputValue);
    }
  }, [inputValue, debounceTime, searchField]);

  const fetchOptions = async (search?: string) => {
    setLoading(true);
    try {
      let queryParams = `$top=${top}&$skip=0&$count=true`;

      // Build filter parts
      const filterParts: string[] = [];

      // Add preFilter if provided
      if (preFilter) {
        filterParts.push(preFilter);
      }

      // Add contains filter if searchField and search term are provided
      if (searchField && search && search.trim()) {
        const escapedSearch = search.replace(/'/g, "''");
        filterParts.push(`contains(${searchField}, '${escapedSearch}')`);
      }

      // Combine filters with 'and'
      if (filterParts.length > 0) {
        const filter = filterParts.join(" and ");
        queryParams += `&$filter=${filter}`;
      }

      const response = await axiosInstance.get(
        `${getBaseUrl()}/${url}?${queryParams}`
      );
      const data = await response.data;
      setOptions(oDataStandard(data.value) ?? []);
    } catch (err) {
      console.error("Failed to fetch options:", err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch options when dropdown opens or search term changes (if searchField is provided)
  React.useEffect(() => {
    if (open && searchField) {
      // If there's a search term, use it; otherwise fetch without filter
      fetchOptions(searchTerm || undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, open, searchField]);

  // Preserve inputValue when options become empty (no results found)
  React.useEffect(() => {
    // If options become empty but we have inputValue in ref, restore it
    // This happens when search returns no results and Autocomplete tries to reset
    if (
      options.length === 0 &&
      inputValueRef.current &&
      !loading &&
      open &&
      inputValue !== inputValueRef.current
    ) {
      // Restore the inputValue from ref
      setInputValue(inputValueRef.current);
    }
  }, [options, inputValue, loading, open]);

  return (
    <>
      <Autocomplete
        sx={{ width: "100%" }}
        open={open}
        onOpen={() => {
          setOpen(true);
          if (!searchField) {
            fetchOptions();
          }
        }}
        onClose={(event, reason) => {
          setOpen(false);
          // Preserve inputValue when closing without selection
          if (reason === "blur" || reason === "toggleInput") {
            // Don't reset inputValue
          }
        }}
        isOptionEqualToValue={(option, val) =>
          option[fieldNameId] === val[fieldNameId]
        }
        getOptionLabel={(option) => option[fieldNameDescription] || ""}
        options={options}
        loading={loading}
        value={selectedOption}
        inputValue={inputValue}
        onInputChange={(_, newInputValue, reason) => {
          // Handle different reasons for input change
          if (reason === "clear") {
            // User clicked clear button - allow reset
            setInputValue("");
            inputValueRef.current = "";
          } else if (reason === "reset") {
            // Autocomplete is trying to reset (e.g., after selection or when no results)
            // Don't restore if we're currently selecting a value
            if (isSelectingRef.current) {
              // Allow reset when selecting
              setInputValue(newInputValue);
              inputValueRef.current = newInputValue;
            } else if (
              inputValueRef.current &&
              newInputValue !== inputValueRef.current &&
              newInputValue === ""
            ) {
              // Restore the inputValue from ref only if we're not selecting and ref has a value
              setInputValue(inputValueRef.current);
            } else {
              // Allow reset if ref is empty
              setInputValue(newInputValue);
              inputValueRef.current = newInputValue;
            }
          } else {
            // Update inputValue for typing, input, etc.
            setInputValue(newInputValue);
            inputValueRef.current = newInputValue;
          }
        }}
        onChange={(_, newValue) => {
          // Set flag to indicate we're selecting a value
          isSelectingRef.current = !!newValue;
          onChange(newValue ? newValue[fieldNameId] : null);
          // Reset inputValue only when a value is actually selected
          if (newValue) {
            setInputValue("");
            inputValueRef.current = "";
          }
          // Reset flag after a short delay
          setTimeout(() => {
            isSelectingRef.current = false;
          }, 100);
        }}
        // Add freeSolo to allow typing even when no options match
        freeSolo={!!searchField}
        // Prevent clearing input when no results
        clearOnBlur={false}
        // Disable internal filtering since we do server-side filtering
        filterOptions={(x) => x}
        // Prevent auto-selection
        selectOnFocus={false}
        // Don't clear on escape
        clearOnEscape={false}
        renderInput={(params) => (
          <TextField
            {...params}
            label={intl.formatMessage({ id: label })}
            required={isRequired}
            error={!!error}
            helperText={error}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </>
  );
}
